import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-inline-hint>` says about itself.
 *
 * The simplest piece in the group and the one with the least to configure: it takes no attributes
 * at all. Everything it needs is the text put inside it. That is a real statement rather than an
 * omission — there is no intent, no size and no variant, because a hint is always the same thing:
 * the info glyph from the project's single icon vocabulary, followed by muted guidance, at the size
 * that sits under a field without competing with it.
 *
 * The glyph is built as SVG through the DOM rather than injected as markup, so a strict CSP is
 * satisfied without any exception, and it inherits the accent through `currentColor`.
 *
 * One gap worth knowing about: the hint is NOT programmatically tied to the field above it. It is
 * plain text with no id and no `aria-describedby` wiring, so a screen reader reads it in document
 * order and not as part of the field's description. On a form where that matters, the association
 * has to be made by the surrounding page.
 */
const docs: ComponentDocs = {
  aliases: ['help text', 'helper text', 'field hint', 'description text', 'info note', 'caption'],

  answers: [
    'explain what a field drives',
    'say what a setting affects downstream',
    'add a note under a control without making somebody hover for it',
    'word the same setting differently on two surfaces',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  // Read from the source: the component observes no attributes and has no properties. Everything it
  // draws comes from the text inside it.
  inputs: [],

  events: [],

  state: [],

  slots: [{ name: '', takes: 'The guidance text. A sentence or two — it wraps, and the glyph stays with the first line.' }],

  tokens: [
    { token: '--caos-color-text-muted', controls: 'The guidance text.' },
    { token: '--caos-color-accent', controls: 'The info glyph — the one part of the hint that moves with a tenant\'s brand.' },
    { token: '--caos-font', controls: 'The typeface the text is set in.' },
    { token: '--caos-space-2', controls: 'The gap between the glyph and the text.' },
  ],

  parts: [
    { part: 'hint', is: 'the hint row' },
    { part: 'icon', is: 'the info glyph' },
  ],

  methods: [],

  placing:
    'Directly under the control it explains, inside the same section, before the next field. It is a ' +
    'block and takes the full width it is given, so in a two-column form it should sit in the column ' +
    'the field is in rather than spanning both. It carries no margin of its own — the gap between the ' +
    'field and the hint belongs to the form.',

  whenToUse:
    'Guidance about one field or setting that is worth permanent space: what it drives, what it ' +
    'affects further down, what the surface calls it. Reach for it when the alternative is a person ' +
    'guessing and finding out later.',

  insteadUse: [
    {
      name: 'Tooltip',
      when:
        'The note is worth having available but not worth permanent space — a definition, an aside, ' +
        'something the regular user already knows. A tooltip has to be asked for, which is the whole ' +
        'trade: it costs no room and it is missed by anyone who does not hover.',
    },
    {
      name: 'BaseField',
      when:
        'The note is a VALIDATION message rather than guidance. Every field draws its own message under ' +
        'itself and turns it red when invalid, and putting an error in a hint means it neither turns ' +
        'red nor clears when the problem is fixed.',
    },
    {
      name: 'NoticeBanner',
      when:
        'What needs saying is about the whole page or section rather than one field, or needs a ' +
        'decision. A hint is never a control and has nowhere to put one.',
    },
    {
      name: 'KeyValueRow',
      when: 'The line is a VALUE to read rather than guidance about entering one.',
    },
  ],

  accessibility:
    'The glyph is hidden from assistive tech, so the hint reads as exactly its text and nothing else — ' +
    'no announced icon name, no stray graphic. Left to the author, and this is the part not to skip: ' +
    'the hint is not attached to the field it explains, so a screen reader meets it after the field ' +
    'rather than as part of it. Where the association matters, give the hint an id and point the ' +
    'field\'s `aria-describedby` at it from the page. The text itself also has to stand alone — "must ' +
    'be a positive number" read out with no idea which field it belongs to helps nobody.',

  examples: [
    {
      key: 'field-hint',
      label: 'Under a field',
      summary:
        'The ordinary case: one line saying what the field above it drives, worded for the surface it ' +
        'appears on rather than for the data model.',
      placement: {
        component: 'caos-inline-hint',
        children: ['Drives the due date shown on the order confirmation.'],
      },
    },
    {
      key: 'wrapping',
      label: 'Longer guidance',
      summary:
        'What happens when the note runs to several lines — the glyph stays level with the first line ' +
        'instead of centring itself against the whole block, so the text keeps a straight left edge.',
      placement: {
        component: 'caos-inline-hint',
        children: [
          'Overriding this value pins it for every revision of the record, including ones created after ' +
          'today. The remaining figures are still recalculated around it, so the totals below will ' +
          'move when other inputs change.',
        ],
      },
    },
  ],
};

export default docs;
